import _ from 'lodash'
import { createRef } from '@wix/materializer'
import queryMappingMap from '@wix/document-services-json-schemas/dist/queryMapping.json'
import { DataMapName, QueryProperty, Language, pointers } from '@wix/thunderbolt-becky-types'
import { GHOST_COMP_TYPE } from '@wix/thunderbolt-commons'

const {
	data: {
		DATA_MAPS: {
			LAYOUT,
			STYLE,
			VARIABLES,
			TRANSFORMATIONS,
			VARIANTS,
			TRANSITIONS,
			REACTIONS,
			PATTERNS,
			DESIGN,
			EFFECTS,
			STATES,
		},
	},
} = pointers

/** namespaces */
export const SITE_NAMESPACE = 'site' as const
export const RESOLVED_REFS_NAMESPACE = 'resolvedByRef' as const
export const ENVIRONMENT_NAMESPACE = 'environment' as const
export const INVALIDATIONS_NAMESPACE = 'widget_invalidations'

/** aspects */
export const DELETED_REF_COMPS_ASPECT = 'deletedRefComponents' as const
export const EXPERIMENTS_ASPECT = 'experiments' as const
// map refComp id to template data (key: appDefinitionId_widgetId, rootCompId: string, isSharedPart: boolean)
export const REF_COMPONENTS_TEMPLATE_DATA_ASPECT = 'refComponentsTemplateData' as const
// map component id to structure with slots in it
export const COMPONENTS_WITH_SLOTS_ASPECT = 'componentStructureWithSlots' as const
// map inflatedRefComp id to it's own presets and ancestors resolved presets
export const RESOLVED_REF_COMP_PRESETS_ASPECT = 'resolvedRefCompPresets' as const
export const STRUCTURE_ASPECT = 'structure' as const
export const RESOLVED_REF_IS_GHOST_COMPONENT_ASPECT = 'isGhostComponent' as const
// map data query to comp id (needed for connectionList dataNode)
export const DATA_QUERY_TO_COMP_ID_ASPECT = 'originalDataQueryToCompId' as const
// map original comp id to inflated comp id (used by ObsoleteBehaviorsList dataNode). Relevant only to internal refs
export const COMP_ID_TO_INFLATED_COMP_ID_ASPECT = 'originalCompIdToInflatedCompId' as const
// stores translations per namespace (site / widget template)
export const RAW_TRANSLATIONS_ASPECT = 'rawTranslations' as const
// stores template root comp id
export const ROOT_COMP_ID_ASPECT = 'rootCompId' as const
// stores resolved translations based on the language and original language env vars
export const RESOLVED_TRANSLATIONS_ASPECT = 'resolvedTranslations' as const
// stores all system styles defined in masterPage theme_data map
export const SYSTEM_STYLES = 'systemStyle' as const
// stores resolved ref components with template rootCompId as a child
export const RESOLVED_REF_COMP = 'resolvedRefComp' as const
// stores inflated refArrays
export const INFLATED_MAP = 'InflatedMap' as const
export const VARIABLES_INFLATED_MAP = `variablesQuery${INFLATED_MAP}`
export const LAYOUT_INFLATED_MAP = `layoutQuery${INFLATED_MAP}`
export const STYLE_INFLATED_MAP = `styleId${INFLATED_MAP}`
export const TRANSFORMATION_INFLATED_MAP = `transformationId${INFLATED_MAP}`
export const VARIANTS_INFLATED_MAP = `variantId${INFLATED_MAP}`
export const REACTIONS_INFLATED_MAP = `reactionId${INFLATED_MAP}`
export const TRANSITION_INFLATED_MAP = `transitionId${INFLATED_MAP}`
export const PATTERNS_INFLATED_MAP = `patternsQuery${INFLATED_MAP}`
export const DESIGN_INFLATED_MAP = `designQuery${INFLATED_MAP}`
export const EFFECTS_INFLATED_MAP = `effectsQuery${INFLATED_MAP}`

export const STATES_INFLATED_MAP = `statesQuery${INFLATED_MAP}`

// Structure invalidation aspect
export const INVALIDATE_STRUCTURE = 'invalidateStructure'

export const INFLATED_DATA = 'inflatedData' as const

/** refs */
export const KEEP_GHOST_COMPONENTS_REF = createRef<boolean>([
	ENVIRONMENT_NAMESPACE,
	ENVIRONMENT_NAMESPACE,
	'keepGhostComponents',
])
export const LANGUAGE_REF = createRef<Language>([ENVIRONMENT_NAMESPACE, ENVIRONMENT_NAMESPACE, 'language'])
export const ORIGINAL_LANGUAGE_REF = createRef<Language>([
	ENVIRONMENT_NAMESPACE,
	ENVIRONMENT_NAMESPACE,
	'originalLanguage',
])

/** consts */
export const QUERY_MAPPING = queryMappingMap as Record<QueryProperty, DataMapName>
export const DATA_MAP_TO_QUERY = _.invert(queryMappingMap) as Record<DataMapName, QueryProperty>
export const DATA_MAP_NAMES = new Set(Object.values(queryMappingMap) as Array<DataMapName>)
export const EMPTY_SCHEMA = Symbol.for('EMPTY_SCHEMA')

export const OVERRIDES_MAP_DELIMITER = '_OM_'
export const LEAFS_OVERRIDES_DELIMITER = '$$$$'
export const GHOST_COMP_STRUCTURE_OVERRIDES = {
	components: [],
	componentType: GHOST_COMP_TYPE,
	connectionQuery: '', // remove once specs.thunderbolt.remove_queries_from_ghost_comps is merged
	uiType: '',
	slotsQuery: '', // remove slotsQuery pointer to avoid resolving slots for ghost comps in flattenStructure
	// layout: {}, // uncomment once specs.thunderbolt.remove_queries_from_ghost_comps is merged
}
export const PRESET_RESOLVING_DATA_ITEM_TYPE = {
	[LAYOUT]: 'layout',
	[STYLE]: 'style',
	[VARIABLES]: 'variable',
	[TRANSFORMATIONS]: 'transformation',
	[VARIANTS]: 'variant',
	[TRANSITIONS]: 'transition',
	[REACTIONS]: 'reaction',
	[PATTERNS]: 'patterns',
	[EFFECTS]: 'effect',
	[STATES]: 'effect',
	[DESIGN]: 'design',
}

export const INFLATED_MAP_TO_DATA_MAP_NAME: Record<string, DataMapName> = {
	[LAYOUT_INFLATED_MAP]: LAYOUT,
	[STYLE_INFLATED_MAP]: STYLE,
	[VARIABLES_INFLATED_MAP]: VARIABLES,
	[TRANSFORMATION_INFLATED_MAP]: TRANSFORMATIONS,
	[VARIANTS_INFLATED_MAP]: VARIANTS,
	[TRANSITION_INFLATED_MAP]: TRANSITIONS,
	[REACTIONS_INFLATED_MAP]: REACTIONS,
	[PATTERNS_INFLATED_MAP]: PATTERNS,
	[DESIGN_INFLATED_MAP]: DESIGN,
	[EFFECTS_INFLATED_MAP]: EFFECTS,
	[STATES_INFLATED_MAP]: STATES,
}

export const HAS_REMOTE_OVERRIDE = 'hasRemoteOverride'
export const HAS_SITE_OVERRIDE = 'hasSiteOverride'
export const SHARED_PARTS_COMP_ID_TO_ROOT_ID = 'sharedPartsCompIdToRootId'

// Editor only map
export const COMP_ID_TO_WIDGET_TEMPLATE_KEY = 'compIdToTemplateKey'
export const VARIANTS_TYPES_TO_INFLATE = ['State', 'Trigger', 'RepeaterPattern', 'Hover', 'Selected']

export const ID_SEPARATOR = '-'
export const MOBILE_PROPERTY_SUFFIX = `${ID_SEPARATOR}mobile`
